import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    CanDeactivate,
    CanLoad,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import {
    ConditionValidationService,
    PermissionResponse,
} from '@penpencil/common';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CoreService } from '../core.service';
import { PermissionService } from './permission.service';
import { PpConditionConfig } from './permission.types';

@Injectable({
    providedIn: 'root',
})
export class PermissionGuard
    implements CanActivate, CanActivateChild, CanDeactivate<unknown>, CanLoad
{
    permissions = [];

    constructor(
        private _permissionService: PermissionService,
        private _coreService: CoreService,
        private _conditionValidationService: ConditionValidationService,
        private _userService: UserService,
        private _router: Router,
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        const permission = route?.data?.permission || '';
        if (!permission) {
            return of(true);
        }
        return this.check(permission).pipe(
            switchMap((hasPermission: any) => {
                if (hasPermission) {
                    return of(true);
                } else {
                    return of(this._router.parseUrl('/dashboard'));
                }
            }),
        );
    }
    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        return true;
    }
    canDeactivate(
        component: unknown,
        currentRoute: ActivatedRouteSnapshot,
        currentState: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        return true;
    }
    canLoad(
        route: Route,
        segments: UrlSegment[],
    ):
        | Observable<boolean | UrlTree>
        | Promise<boolean | UrlTree>
        | boolean
        | UrlTree {
        const permission = route.data.permission;
        return this.check(permission);
    }

    check(permission: string): Observable<boolean> {
        let isSuperAdmin = false;

        return this._userService.user$.pipe(
            switchMap((user: User) => {
                isSuperAdmin = user?.roles?.some(
                    (role: any) =>
                        role.name === 'Super Admin' ||
                        role.id === '6893a3ef2c44d931a52adfe1',
                );

                // allowed all permission for super admin
                if (isSuperAdmin) {
                    return of(null);
                }

                const query = user?.roles?.some(
                    (role: any) => role.name === 'Teacher',
                )
                    ? { level: 'user' }
                    : {};
                // Use cached permissions if available
                const cachedPermissions = this._permissionService
                    .getPermissions()
                    .getValue();

                return cachedPermissions?.length
                    ? of({ data: cachedPermissions })
                    : this._coreService.permissions(query);
            }),
            switchMap((permissions: PermissionResponse | null) => {
                if (!permissions?.data) {
                    return of(isSuperAdmin);
                }

                this.permissions = permissions.data.map((el: any) =>
                    el?.actionName ? el?.actionName : el,
                );

                return of(
                    this.permissions.length
                        ? this.verifyPermission(null, permission)
                        : isSuperAdmin,
                );
            }),
            catchError((error) => {
                console.error('Permission check error:', error);
                return of(false);
            }),
        );
    }

    verifyPermission(
        condition: PpConditionConfig,
        permission: string,
    ): boolean {
        const data = {
            permissions: this.permissions,
            permission: permission,
        };
        condition = this._permissionService.createCondition(permission);
        if (this._conditionValidationService.displayValidate(condition, data)) {
            return true;
        } else {
            return false;
        }
    }
}
